import { Component, Inject, OnDestroy, OnInit, Optional } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Utils } from "formiojs";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute } from "@angular/router";
import { FormioLoader, FIELD_TYPE, FormioTranslate, ResourceNameService, TRANSLATIONS, ErrorToast } from "dp-formio";

@Component({
  selector: "add-edit-resource-dialog",
  templateUrl: "add-edit-dialog.component.html",
  styleUrls: ["./add-edit-dialog.component.scss"],
  providers: [FormioLoader],
})
export class AddEditResourceDialog implements OnInit, OnDestroy {
  public get ENUM_FIELD_TYPE(): typeof FIELD_TYPE {
    return FIELD_TYPE;
  }

  formControls: FormGroup = new FormGroup({});
  parentId: string;

  private subs: Subscription[] = [];
  set sub(s: Subscription) {
    this.subs.push(s);
  }

  constructor(
    private toastrService: ToastrService,
    @Optional() private translate: FormioTranslate,
    private formloader: FormioLoader,
    private resourceNameService: ResourceNameService,
    public dialogRef: MatDialogRef<AddEditResourceDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public fb: FormBuilder,
    private route: ActivatedRoute
  ) {
    this.formloader.loading = false;
    this.data.fields.forEach((control) => {
      let maxValidator = Validators.nullValidator;
      let minValidator = Validators.nullValidator;

      if (control.max || control.max === 0) {
        if (typeof control.max == "string")
          maxValidator = Validators.max(
            Utils.evaluate(control.max, { data: this.data.data })
          );
        else maxValidator = Validators.max(control.max);
      }
      if (control.min || control.min === 0) {
        if (typeof control.min == "string")
          minValidator = Validators.min(
            Utils.evaluate(control.min, { data: this.data.data })
          );
        else minValidator = Validators.min(control.min);
      }

      if (control.type === FIELD_TYPE.SELECT && this.data.isAdd) {
        this.getResourceData(control);
      }

      this.formControls.addControl(
        control.name,
        this.fb.control(
          {
            value: control.value || "",
            disabled: data.isView || (!data.isAdd && control.name === "key"),
          },
          [
            control.required ? Validators.required : Validators.nullValidator,
            maxValidator,
            minValidator,
          ]
        )
      );
    });
    console.log(this.formControls);
  }

  onInputChanged(item, event) {
    item.value = event.target?.value;
    if (item.name === "order") {
      this.data.order = +event.target?.value;
    }
  }

  getResourceData(control: any) {
    this.formloader.loading = true;
    this.sub = this.resourceNameService
      .loadCustomByKey(control.resource)
      .subscribe(
        (data) => {
          control.dataSource = data.sort(function (a, b) {
            if (a.data.order < b.data.order) {
              return -1;
            } else if (a.data.order > b.data.order) {
              return 1;
            } else {
              return 0;
            }
          });
          this.formloader.loading = false;
        },
        (error) => {
          this.formloader.loading = false;
        }
      );
  }

  onSaveResource() {
    this.formloader.loading = true;

    this.resourceNameService
      .updateTranslation({
        key: this.formControls.controls.key.value,
        nameAr: this.getTranslations(TRANSLATIONS.NAMEAR) || "",
        nameEn: this.getTranslations(TRANSLATIONS.NAMEEN) || "",
      })
      .subscribe(
        (translations: any[]) => {
          if (translations && translations.length) {
            const resourceId = this.data.isAdd === false ? this.data._id : "";

            if (typeof this.data.isParentNode === "string") {
              this.getParentKey(this.data.isParentNode, resourceId);
            } else {
              this.resourceNameService
                .saveResourceData(
                  this.data.isAdd,
                  this.data.resourceName,
                  {
                    ...this.excludeTranslationsFromBody(
                      this.formControls.value
                    ),
                    [this.data.parentId]:
                      typeof this.data.isParentNode === "string"
                        ? this.data.isParentNode
                        : "",
                  },
                  resourceId
                )
                .subscribe(
                  (res: any) => {
                    this.formloader.loading = true;
                    if (res && res.data) {
                      this.data._id = res._id;
                      this.dialogRef.close(this.data);
                    }
                  },
                  (error: any) => {
                    this.formloader.loading = false;

                    error.error.details.forEach((error) => {
                      this.showError(
                        this.translate.instant("ErrorOccurred"),
                        error.message
                      );
                    });
                  }
                );
            }
          }
        },
        (err: any) => (this.formloader.loading = false)
      );
  }

  getParentKey(id, resourceId) {
    const resourceKey = this.route.snapshot.queryParams.resource;

    let item: any;
    this.resourceNameService.loadCustomByKeyId(resourceKey, id).subscribe(
      (res: any) => {
        item = {
          data: {
            ...res.data,
          },
        };
      },
      () => {},
      () => {
        this.resourceNameService
          .saveResourceData(
            this.data.isAdd,
            this.data.resourceName,
            {
              ...this.excludeTranslationsFromBody(this.formControls.value),
              [this.data.parentId]:
                typeof this.data.isParentNode === "string" ? { ...item } : "",
            },
            resourceId
          )
          .subscribe(
            (res: any) => {
              this.formloader.loading = true;
              if (res && res.data) {
                this.data._id = res._id;
                this.dialogRef.close(this.data);
              }
            },
            (error: any) => {
              this.formloader.loading = false;

              error.error.details.forEach((error) => {
                this.showError(
                  this.translate.instant("ErrorOccurred"),
                  error.message
                );
              });
            }
          );
      }
    );
  }

  getTranslations(lang: TRANSLATIONS): string {
    const selectedLang = lang;

    const formControl: any = Object.keys(this.formControls.controls).filter(
      (control) => control.includes(selectedLang)
    );

    if (formControl) {
      return this.formControls.controls[formControl].value;
    }
  }

  excludeTranslationsFromBody(formControls: {}): {} {
    const body = {
      key:
        this.data.isAdd == false ? this.formControls.controls["key"].value : "",
    };

    Object.keys(formControls).map((pro: string) => {
      if (
        !pro.includes(TRANSLATIONS.NAMEAR) &&
        !pro.includes(TRANSLATIONS.NAMEEN)
      ) {
        body[pro] = formControls[pro];
      }
    });

    return body;
  }

  private showError(title: string, message: string) {
    this.toastrService.show(message, title, {
      toastClass: "notification-toast",
      closeButton: true,
      enableHtml: true,
      toastComponent: ErrorToast,
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }
}
